// GPX → coloured map segments.
//
// Server-side `parseGpx` (in api/services/gpx-edit.ts) is the authoritative
// parser; the web client consumes structured { track, waypoints } via
// GET /api/routes/:id/geometry. This file's `parseGpx` exists only as a
// fallback for the rare cases where we have a raw GPX text in-hand (e.g.
// pre-upload preview); production routes use the server JSON.

import type { FeatureCollection, LineString, Feature, Position } from 'geojson'
import type { TrackPoint as SchemaTrackPoint, Waypoint } from '@xingroute/schema'
import { gpxElapsedSeconds, singleGpxSegment, Timestamp } from '@xingroute/schema'

export interface TrackPoint extends SchemaTrackPoint {
  time?: string
}

export interface TrackStats {
  pointCount: number
  distanceKm: number
  ascentM: number
  descentM: number
  minEle?: number
  maxEle?: number
  durationSeconds?: number
}

export function parseGpx(text: string): TrackPoint[] {
  const xml = text.trim()
  if (!xml || typeof DOMParser === 'undefined') return []

  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) return []

  const segments: TrackPoint[][] = []
  const gpx = doc.documentElement
  if (gpx.localName !== 'gpx') return []
  for (const trk of Array.from(gpx.children).filter((child) => child.localName === 'trk')) {
    for (const trkseg of Array.from(trk.children).filter((child) => child.localName === 'trkseg')) {
      const segment: TrackPoint[] = []
      for (const trkpt of Array.from(trkseg.children).filter(
        (child) => child.localName === 'trkpt',
      )) {
        const lat = Number.parseFloat(trkpt.getAttribute('lat') ?? '')
        const lon = Number.parseFloat(trkpt.getAttribute('lon') ?? '')
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue

        const eleText = directChildText(trkpt, 'ele')
        const eleParsed = eleText !== undefined ? Number.parseFloat(eleText) : undefined
        const time = directChildText(trkpt, 'time')
        segment.push({
          lon,
          lat,
          ele: eleParsed !== undefined && Number.isFinite(eleParsed) ? eleParsed : undefined,
          ...(time && Timestamp.safeParse(time).success ? { time } : {}),
        })
      }
      segments.push(segment)
    }
  }
  return singleGpxSegment(segments)
}

function directChildText(element: Element, localName: string): string | undefined {
  for (const child of Array.from(element.children)) {
    if (child.localName !== localName) continue
    const text = child.textContent?.trim()
    return text || undefined
  }
  return undefined
}

export function toGpx(
  points: TrackPoint[],
  name = 'XingRoute preview',
  waypoints: Waypoint[] = [],
): string {
  const wpts = waypoints.map(waypointToGpx).join('\n')
  const trkpts = points
    .map((point) => {
      const ele = point.ele !== undefined ? `\n        <ele>${formatNumber(point.ele)}</ele>` : ''
      const time = point.time ? `\n        <time>${escapeXml(point.time)}</time>` : ''
      return `      <trkpt lat="${formatNumber(point.lat)}" lon="${formatNumber(point.lon)}">${ele}${time}\n      </trkpt>`
    })
    .join('\n')
  const waypointBlock = wpts ? `\n${wpts}` : ''
  const xrNamespace =
    waypoints.length > 0 ? ' xmlns:xr="http://xingroute.app/schema/gpx-ext/0"' : ''
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="XingRoute" xmlns="http://www.topografix.com/GPX/1/1"${xrNamespace}>${waypointBlock}
  <trk>
    <name>${escapeXml(name)}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>
`
}

export function reverseGpxTrackOrder(text: string, waypoints?: Waypoint[]): string | null {
  const xml = text.trim()
  if (!xml || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return null
  const doc = new DOMParser().parseFromString(xml, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length > 0) return null

  const gpx = doc.getElementsByTagNameNS('*', 'gpx')[0]
  if (!gpx) return null

  const tracks = Array.from(gpx.children).filter((child) => child.localName === 'trk')
  for (const trk of tracks) {
    const segments = Array.from(trk.children).filter((child) => child.localName === 'trkseg')
    for (const segment of segments) {
      const points = Array.from(segment.children).filter((child) => child.localName === 'trkpt')
      for (const point of points.reverse()) segment.appendChild(point)
    }
    for (const segment of segments.reverse()) trk.appendChild(segment)
  }
  for (const trk of tracks.reverse()) gpx.appendChild(trk)

  if (waypoints) {
    for (const wpt of Array.from(gpx.children).filter((child) => child.localName === 'wpt')) {
      gpx.removeChild(wpt)
    }
    const waypointDoc = new DOMParser().parseFromString(
      toGpx([], 'waypoints', waypoints),
      'application/xml',
    )
    const insertBefore = Array.from(gpx.children).find((child) => child.localName === 'trk') ?? null
    for (const wpt of Array.from(waypointDoc.getElementsByTagNameNS('*', 'wpt'))) {
      gpx.insertBefore(doc.importNode(wpt, true), insertBefore)
    }
  }
  return new XMLSerializer().serializeToString(doc)
}

function waypointToGpx(waypoint: Waypoint): string {
  const body = [
    waypoint.ele !== undefined ? `    <ele>${formatNumber(waypoint.ele)}</ele>` : '',
    waypoint.name ? `    <name>${escapeXml(waypoint.name)}</name>` : '',
    waypoint.desc ? `    <desc>${escapeXml(waypoint.desc)}</desc>` : '',
    waypoint.sym ? `    <sym>${escapeXml(waypoint.sym)}</sym>` : '',
    '    <extensions>',
    `      <xr:id>${escapeXml(waypoint.id)}</xr:id>`,
    waypoint.name_en ? `      <xr:name_en>${escapeXml(waypoint.name_en)}</xr:name_en>` : '',
    waypoint.desc_en ? `      <xr:desc_en>${escapeXml(waypoint.desc_en)}</xr:desc_en>` : '',
    waypoint.note ? `      <xr:note>${escapeXml(waypoint.note)}</xr:note>` : '',
    waypoint.captured_at
      ? `      <xr:captured_at>${escapeXml(waypoint.captured_at)}</xr:captured_at>`
      : '',
    waypoint.distance_along_track !== undefined
      ? `      <xr:distance_along_track>${formatNumber(waypoint.distance_along_track)}</xr:distance_along_track>`
      : '',
    waypoint.kind ? `      <xr:kind>${escapeXml(waypoint.kind)}</xr:kind>` : '',
    '    </extensions>',
  ].filter(Boolean)
  return `  <wpt lat="${formatNumber(waypoint.lat)}" lon="${formatNumber(waypoint.lon)}">\n${body.join('\n')}\n  </wpt>`
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(7).replace(/\.?0+$/, '')
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

const R = 6_371_000
function toRad(d: number): number {
  return (d * Math.PI) / 180
}
function haversine(a: TrackPoint, b: TrackPoint): number {
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

export function trackStats(points: TrackPoint[]): TrackStats {
  let distance = 0
  let ascent = 0
  let descent = 0
  let minEle: number | undefined
  let maxEle: number | undefined
  const durationSeconds = gpxElapsedSeconds([points])
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!
    if (p.ele !== undefined) {
      minEle = minEle === undefined ? p.ele : Math.min(minEle, p.ele)
      maxEle = maxEle === undefined ? p.ele : Math.max(maxEle, p.ele)
    }
    if (i === 0) continue
    const prev = points[i - 1]!
    distance += haversine(prev, p)
    if (prev.ele !== undefined && p.ele !== undefined) {
      const d = p.ele - prev.ele
      if (d > 0) ascent += d
      else descent -= d
    }
  }
  return {
    pointCount: points.length,
    distanceKm: distance / 1000,
    ascentM: ascent,
    descentM: descent,
    minEle,
    maxEle,
    ...(durationSeconds !== undefined ? { durationSeconds } : {}),
  }
}

// Elevation smoothing radius in metres. Each point's elevation is replaced by
// the mean of all points within ±this distance along the path — a *distance*
// window (not a point-count window), so it is independent of sampling density
// (sparse tracks aren't over-flattened, dense ones are still smoothed).
// Mirrors iOS `ElevationStats.smoothedPath` (35m) so both platforms report the
// same ascent / grade for the same imported GPX.
export const ELEVATION_SMOOTHING_RADIUS_M = 35

/**
 * Return `points` with each `ele` replaced by the mean elevation of every point
 * within ±`radiusM` metres along the path (lat/lon, other fields and point
 * count unchanged). Points missing `ele` are skipped inside the window; if the
 * whole window has no elevation the point keeps its original value.
 *
 * Raw per-point GPX elevation carries several metres of noise. `trackStats`
 * (adjacent Δele sum) and `smoothGrades` (Δele/Δrun) amplify that noise, so a
 * gently-descending imported track reads a wildly inflated ascent and paints a
 * rainbow of spurious steep grades. Smoothing the elevation *before* those raw
 * primitives removes the noise while leaving the primitives pure (they stay
 * unit-tested on raw input). Apply this only at the display boundary — the same
 * way iOS keeps its primitives raw and smooths at the UI edge.
 */
export function smoothPathElevation(
  points: TrackPoint[],
  radiusM = ELEVATION_SMOOTHING_RADIUS_M,
): TrackPoint[] {
  if (points.length <= 2) return points
  const prefix: number[] = new Array(points.length)
  prefix[0] = 0
  for (let i = 1; i < points.length; i++) {
    prefix[i] = prefix[i - 1]! + haversine(points[i - 1]!, points[i]!)
  }
  const out = points.map((p) => ({ ...p }))
  let lo = 0
  let hi = 0
  for (let i = 0; i < points.length; i++) {
    while (lo < i && prefix[i]! - prefix[lo]! > radiusM) lo++
    if (hi < i) hi = i
    while (hi < points.length - 1 && prefix[hi + 1]! - prefix[i]! <= radiusM) hi++
    let sum = 0
    let n = 0
    for (let j = lo; j <= hi; j++) {
      const e = points[j]!.ele
      if (e !== undefined) {
        sum += e
        n++
      }
    }
    if (n > 0) out[i]!.ele = sum / n
  }
  return out
}

// Komoot-style 6-bucket discrete grade palette.
//
// Why discrete buckets instead of continuous interpolation?
//   1. GPS elevation noise (±5m typical) over short GPS sampling distances
//      (a few metres) produces wildly oscillating per-segment instantaneous
//      grades. A continuous HCL interpolation faithfully renders that noise
//      as a busy "rainbow" along the trail; humans then have a hard time
//      reading the actual difficulty profile.
//   2. Industry-standard tools (Komoot, Strava, Ride with GPS) all converged
//      on discrete bucketed colours combined with grade smoothing because
//      this matches how outdoor users read a route: "where are the steep
//      sections?". A bucket boundary is a sharp answer.
//
// The fix has two parts and they go together:
//   - `smoothGrades(points, 50)` — centered moving average over a 50m window
//     before bucketing.
//   - `GRADE_BUCKETS` — 6 hiking-friendly bins.
//
// Map polyline (`RouteMap.tsx`) reads `properties.grade` via a Mapbox
// `step` paint expression keyed off the same boundaries. The elevation
// profile (`ElevationProfile.tsx`) fills per-segment quads with
// `bucketColor(grade)`. Both surfaces are now visually consistent: the
// section of trail that turns red on the map is the same section that
// turns red under the curve.
export interface GradeBucket {
  /** Inclusive lower bound of this bucket (−Infinity for the first). */
  ge: number
  /** Exclusive upper bound (Infinity for the last). */
  lt: number
  color: string
  /** Short human label (kept locale-neutral; the legend wraps it). */
  label: string
}

export const GRADE_BUCKETS: readonly GradeBucket[] = [
  { ge: -Infinity, lt: -7, color: '#005995', label: '<-7' },
  { ge: -7, lt: -3, color: '#5ba3d6', label: '-7~-3' },
  { ge: -3, lt: 3, color: '#9ea7af', label: '±3' },
  { ge: 3, lt: 7, color: '#f5b800', label: '3~7' },
  { ge: 7, lt: 12, color: '#e8721e', label: '7~12' },
  { ge: 12, lt: Infinity, color: '#c43030', label: '>12%' },
]

/** Numeric thresholds [−7, −3, 3, 7, 12] derived from `GRADE_BUCKETS`. */
export const GRADE_BUCKET_STOPS: readonly number[] = GRADE_BUCKETS.slice(1).map(
  (bucket) => bucket.ge,
)

/** Fallback for segments without ele data (matches `RouteMap.tsx` trail color). */
export const GRADE_FALLBACK_COLOR = 'rgba(107, 114, 128, 0.45)'

/**
 * Returns the bucket colour for a smoothed grade percentage. `null` /
 * non-finite input → `fallback` so callers don't have to special-case.
 */
export function bucketColor(grade: number | null, fallback = GRADE_FALLBACK_COLOR): string {
  if (grade === null || !Number.isFinite(grade)) return fallback
  for (const bucket of GRADE_BUCKETS) {
    if (grade >= bucket.ge && grade < bucket.lt) return bucket.color
  }
  return GRADE_BUCKETS[GRADE_BUCKETS.length - 1]!.color
}

/**
 * Centered moving-average smoothing of per-segment grade over `windowM`
 * meters. Returns `(number | null)[]` of length `points.length - 1` where
 * index `i` is the smoothed grade for segment `points[i] → points[i+1]`.
 *
 * The smoothing uses a rise/run accumulator over all segments whose centre
 * falls inside `±windowM/2` of the current segment's centre. Segments
 * shorter than 0.5m or missing ele are dropped from the accumulator so they
 * don't pull the average toward 0 artificially.
 *
 * Empirically a 50m window removes the great majority of GPS-noise-driven
 * grade oscillation without erasing real terrain features (~10m of trail).
 */
export function smoothGrades(points: TrackPoint[], windowM = 50): (number | null)[] {
  const n = points.length
  if (n < 2) return []
  // Pre-compute per-segment dist + eleDelta so we can accumulate fast.
  const dists: number[] = new Array(n - 1)
  const rises: (number | null)[] = new Array(n - 1)
  const centers: number[] = new Array(n - 1)
  let cum = 0
  for (let i = 0; i < n - 1; i++) {
    const a = points[i]!
    const b = points[i + 1]!
    const d = haversine(a, b)
    const segStart = cum
    cum += d
    centers[i] = segStart + d / 2
    dists[i] = d
    rises[i] = d > 0.5 && a.ele !== undefined && b.ele !== undefined ? b.ele - a.ele : null
  }
  const half = windowM / 2
  const out: (number | null)[] = new Array(n - 1).fill(null)
  let lo = 0
  let hi = 0
  let totalRise = 0
  let totalRun = 0
  const addSegment = (index: number) => {
    if (rises[index] === null) return
    totalRise += rises[index]!
    totalRun += dists[index]!
  }
  const removeSegment = (index: number) => {
    if (rises[index] === null) return
    totalRise -= rises[index]!
    totalRun -= dists[index]!
  }
  for (let i = 0; i < n - 1; i++) {
    const c = centers[i]!
    while (lo < n - 1 && centers[lo]! < c - half) {
      if (lo < hi) removeSegment(lo)
      lo++
    }
    while (hi < n - 1 && centers[hi]! <= c + half) {
      addSegment(hi)
      hi++
    }
    out[i] = totalRun > 0 ? (totalRise / totalRun) * 100 : null
  }
  return out
}

export type ColouredSegmentsFeatureCollection = FeatureCollection<
  LineString,
  { grade: number | null; startKm: number; endKm: number }
>

/**
 * Build per-segment GeoJSON features carrying smoothed grade for the map
 * polyline. The Mapbox `step` paint expression in `RouteMap.tsx` keys off
 * `properties.grade` and the bucket thresholds in `GRADE_BUCKET_STOPS`.
 */
export function toColouredSegments(
  points: TrackPoint[],
  windowM = 50,
): ColouredSegmentsFeatureCollection {
  const grades = smoothGrades(points, windowM)
  const features: Feature<LineString, { grade: number | null; startKm: number; endKm: number }>[] =
    []
  let cumulativeM = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const dist = haversine(a, b)
    const startKm = cumulativeM / 1000
    cumulativeM += dist
    const endKm = cumulativeM / 1000
    const coords: Position[] = [
      [a.lon, a.lat],
      [b.lon, b.lat],
    ]
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords },
      properties: { grade: grades[i - 1] ?? null, startKm, endKm },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function trackBounds(points: TrackPoint[]): [[number, number], [number, number]] | null {
  if (points.length === 0) return null
  let minLon = Infinity,
    minLat = Infinity,
    maxLon = -Infinity,
    maxLat = -Infinity
  for (const p of points) {
    if (p.lon < minLon) minLon = p.lon
    if (p.lon > maxLon) maxLon = p.lon
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ]
}

// ───────────────────────────────────────────────────────────────────────
// Sub-range helpers — used by `RouteMap` to render the elevation-profile
// segment-selection highlight, and by `RouteElevationSection` to show
// `distance / ascent / descent` for that selected range.
//
// Both helpers walk the same cumulative-haversine index a single time and
// share their interpolation logic: a `(fromM, toM)` window can cut through
// the middle of a track segment, so we linearly interpolate position +
// elevation at the cut points. This keeps the on-map highlight line +
// the reported stats consistent with what the user sees in the chart
// (which uses the same fractional-segment overlap logic).
// ───────────────────────────────────────────────────────────────────────

export interface RangeStats {
  /** Distance covered by the selected window, in km. */
  distanceKm: number
  /** Cumulative climb (m) inside the window only. */
  ascentM: number
  /** Cumulative descent (m, positive) inside the window only. */
  descentM: number
}

function interpolatePoint(a: TrackPoint, b: TrackPoint, t: number): TrackPoint {
  const clamped = Math.min(1, Math.max(0, t))
  const ele =
    a.ele !== undefined && b.ele !== undefined ? a.ele + (b.ele - a.ele) * clamped : undefined
  return {
    lat: a.lat + (b.lat - a.lat) * clamped,
    lon: a.lon + (b.lon - a.lon) * clamped,
    ...(ele !== undefined ? { ele } : {}),
  }
}

/** Slice the polyline whose cumulative haversine distance is inside
 *  `[fromM, toM]`. Endpoints are interpolated when the window starts /
 *  ends mid-segment, but snap to the original vertex when the cut lands
 *  within a small epsilon of a segment boundary (otherwise floating-point
 *  drift would emit a phantom duplicate point at the boundary). Returns
 *  `null` when the input is too short, the window is degenerate, or there
 *  is no overlap. */
export function subTrackByDistance(
  points: TrackPoint[],
  fromM: number,
  toM: number,
): TrackPoint[] | null {
  if (points.length < 2) return null
  const lo = Math.max(0, Math.min(fromM, toM))
  const hi = Math.max(lo, Math.max(fromM, toM))
  if (hi - lo < 1e-6) return null
  // Snap windows that land within 1 mm of a vertex onto that vertex. The
  // chart brush emits distances in km / fractional km, so the relative
  // precision is well above this threshold; the snap exists only to absorb
  // cumulative haversine roundoff (~1 ULP per segment).
  const snapM = 1e-3
  const out: TrackPoint[] = []
  let cumStart = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const segLen = haversine(a, b)
    const cumEnd = cumStart + segLen
    // No overlap yet — keep walking. Treat "within snapM of lo" as touching
    // so we don't double-emit the boundary vertex on the next segment.
    if (cumEnd <= lo + snapM) {
      cumStart = cumEnd
      continue
    }
    // Past the window — done.
    if (cumStart >= hi - snapM) break
    if (segLen > 0) {
      // First contributing segment: interpolate the start point if `lo`
      // cuts well into this segment, else snap to `a`.
      if (out.length === 0) {
        if (lo - cumStart > snapM) {
          const t = (lo - cumStart) / segLen
          out.push(interpolatePoint(a, b, t))
        } else {
          out.push(a)
        }
      }
      // End point for this segment: clip at `hi` if the window ends well
      // inside this segment; otherwise emit `b` exactly.
      if (cumEnd - hi > snapM) {
        const t = (hi - cumStart) / segLen
        out.push(interpolatePoint(a, b, t))
        break
      }
      out.push(b)
    }
    cumStart = cumEnd
  }
  if (out.length < 2) return null
  return out
}

/** Distance / ascent / descent for the slice `[fromM, toM]` of `points`.
 *  Uses fractional-segment overlap so the reported numbers match the
 *  visual extent of the segment-selection brush on the elevation chart. */
export function rangeStatsByDistance(points: TrackPoint[], fromM: number, toM: number): RangeStats {
  if (points.length < 2) return { distanceKm: 0, ascentM: 0, descentM: 0 }
  const lo = Math.max(0, Math.min(fromM, toM))
  const hi = Math.max(lo, Math.max(fromM, toM))
  let distance = 0
  let ascent = 0
  let descent = 0
  let cumStart = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const segLen = haversine(a, b)
    const cumEnd = cumStart + segLen
    const overlapStart = Math.max(lo, cumStart)
    const overlapEnd = Math.min(hi, cumEnd)
    if (overlapEnd <= overlapStart || segLen <= 0) {
      cumStart = cumEnd
      continue
    }
    const overlap = overlapEnd - overlapStart
    distance += overlap
    if (a.ele !== undefined && b.ele !== undefined) {
      const eleDelta = (b.ele - a.ele) * (overlap / segLen)
      if (eleDelta > 0) ascent += eleDelta
      else descent -= eleDelta
    }
    cumStart = cumEnd
  }
  return { distanceKm: distance / 1000, ascentM: ascent, descentM: descent }
}
